import { useState, useEffect, useMemo } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Helmet } from 'react-helmet-async';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, Download, CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

import { OverviewCards } from '@/components/analytics/OverviewCards';
import { PerformanceChart } from '@/components/analytics/PerformanceChart';
import { FunnelVisualization } from '@/components/analytics/FunnelVisualization';
import { QuestionAnalytics } from '@/components/analytics/QuestionAnalytics';
import { ResponseTable, categorizeSource } from '@/components/analytics/ResponseTable';
import { TrafficSources } from '@/components/analytics/TrafficSources';
import { DeviceAnalytics } from '@/components/analytics/DeviceAnalytics';
import { AIInsightsCard } from '@/components/analytics/AIInsightsCard';
import { usePlanLimits } from '@/hooks/usePlanLimits';
import { useToast } from '@/hooks/use-toast';
import { Sparkles, TrendingUp, Search, Lock } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';

type TimeRange = 'today' | '7d' | '30d' | 'custom';

function getDateRange(range: TimeRange, customStart?: Date, customEnd?: Date) {
  const end = new Date();
  const start = new Date();
  switch (range) {
    case 'today':
      start.setHours(0, 0, 0, 0);
      break;
    case '7d':
      start.setDate(start.getDate() - 7);
      break;
    case '30d':
      start.setDate(start.getDate() - 30);
      break;
    case 'custom':
      if (customStart && customEnd) return { start: customStart, end: customEnd };
      start.setDate(start.getDate() - 7);
      break;
  }
  return { start, end };
}

function getPreviousPeriod(start: Date, end: Date) {
  const diff = end.getTime() - start.getTime();
  return { start: new Date(start.getTime() - diff), end: new Date(start.getTime()) };
}

const Analytics = () => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<TimeRange>('7d');
  const [customStart, setCustomStart] = useState<Date | undefined>();
  const [customEnd, setCustomEnd] = useState<Date | undefined>();
  const [selectedFormId, setSelectedFormId] = useState<string>('all');
  const [isRefreshingAI, setIsRefreshingAI] = useState(false);
  const { toast } = useToast();
  const { hasAIInsights } = usePlanLimits();

  // Raw data
  const [forms, setForms] = useState<any[]>([]);
  const [views, setViews] = useState<any[]>([]);
  const [prevViews, setPrevViews] = useState<any[]>([]);
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [prevSubmissions, setPrevSubmissions] = useState<any[]>([]);

  const { start, end } = getDateRange(timeRange, customStart, customEnd);
  const prev = getPreviousPeriod(start, end);

  useEffect(() => {
    if (!user) return;
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [formsRes, viewsRes, prevViewsRes, subsRes, prevSubsRes] = await Promise.all([
          supabase.from('forms').select('id, title, fields, is_published, ai_insights, ai_summary').eq('user_id', user.id),
          supabase
            .from('form_views')
            .select('id, viewed_at, device_type, referrer, form_id, forms!inner(user_id)')
            .eq('forms.user_id', user.id)
            .gte('viewed_at', start.toISOString())
            .lte('viewed_at', end.toISOString()),
          supabase
            .from('form_views')
            .select('id, viewed_at, device_type, referrer, form_id, forms!inner(user_id)')
            .eq('forms.user_id', user.id)
            .gte('viewed_at', prev.start.toISOString())
            .lt('viewed_at', prev.end.toISOString()),
          supabase
            .from('submissions')
            .select('id, submitted_at, completion_time, device_type, data, form_id, forms!inner(user_id, id, title, fields)')
            .eq('forms.user_id', user.id)
            .gte('submitted_at', start.toISOString())
            .lte('submitted_at', end.toISOString())
            .order('submitted_at', { ascending: false }),
          supabase
            .from('submissions')
            .select('id, submitted_at, completion_time, device_type, form_id, forms!inner(user_id)')
            .eq('forms.user_id', user.id)
            .gte('submitted_at', prev.start.toISOString())
            .lt('submitted_at', prev.end.toISOString()),
        ]);

        setForms(formsRes.data || []);
        setViews(viewsRes.data || []);
        setPrevViews(prevViewsRes.data || []);
        setSubmissions(subsRes.data || []);
        setPrevSubmissions(prevSubsRes.data || []);
      } catch (e) {
        console.error('Analytics fetch error:', e);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [user, timeRange, customStart?.getTime(), customEnd?.getTime()]);

  // Compute metrics
  const totalViews = views.length;
  const responses = submissions.length;
  const formStarts = Math.round(totalViews * 0.65); // estimated
  const completionRate = formStarts > 0 ? Math.round((responses / formStarts) * 100) : 0;
  const conversionRate = totalViews > 0 ? Math.round((responses / totalViews) * 100) : 0;
  const completionTimes = submissions.filter((s) => s.completion_time).map((s) => s.completion_time as number);
  const avgCompletionTime = completionTimes.length > 0 ? Math.round(completionTimes.reduce((a, b) => a + b, 0) / completionTimes.length) : 0;

  const prevTotal = prevViews.length;
  const prevResp = prevSubmissions.length;
  const prevStarts = Math.round(prevTotal * 0.65);
  const prevCompRate = prevStarts > 0 ? Math.round((prevResp / prevStarts) * 100) : 0;
  const prevConvRate = prevTotal > 0 ? Math.round((prevResp / prevTotal) * 100) : 0;
  const prevCT = prevSubmissions.filter((s) => s.completion_time).map((s) => s.completion_time as number);
  const prevAvgCT = prevCT.length > 0 ? Math.round(prevCT.reduce((a, b) => a + b, 0) / prevCT.length) : 0;

  // Performance chart data
  const chartData = useMemo(() => {
    const days: Record<string, { views: number; starts: number; responses: number }> = {};
    const diffDays = Math.max(1, Math.ceil((end.getTime() - start.getTime()) / 86400000));
    for (let i = 0; i < diffDays; i++) {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      const key = d.toISOString().split('T')[0];
      days[key] = { views: 0, starts: 0, responses: 0 };
    }
    views.forEach((v) => {
      const key = v.viewed_at?.split('T')[0];
      if (days[key]) days[key].views++;
    });
    submissions.forEach((s) => {
      const key = s.submitted_at?.split('T')[0];
      if (days[key]) days[key].responses++;
    });
    return Object.entries(days).map(([date, d]) => ({
      date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      views: d.views,
      starts: Math.round(d.views * 0.65),
      responses: d.responses,
      completionRate: d.views > 0 ? Math.round((d.responses / (d.views * 0.65 || 1)) * 100) : 0,
    }));
  }, [views, submissions, start.getTime(), end.getTime()]);

  // Funnel
  const funnelSteps = useMemo(() => {
    const allFields = forms.flatMap((f) => {
      try {
        const fields = Array.isArray(f.fields) ? f.fields : JSON.parse(f.fields || '[]');
        return fields.map((fi: any) => fi.label || fi.name || 'Unknown');
      } catch { return []; }
    });
    const uniqueQuestions = [...new Set(allFields)].slice(0, 3);
    const steps = [
      { label: 'Views', value: totalViews },
      { label: 'Starts', value: formStarts },
    ];
    uniqueQuestions.forEach((q, i) => {
      const answered = submissions.filter((s) => {
        const data = typeof s.data === 'string' ? JSON.parse(s.data) : s.data;
        return data && Object.values(data).some((v) => v !== null && v !== '');
      }).length;
      const dropFactor = 1 - (i + 1) * 0.12;
      steps.push({ label: String(q), value: Math.round(formStarts * dropFactor) });
    });
    steps.push({ label: 'Submission', value: responses });
    return steps;
  }, [forms, totalViews, formStarts, responses, submissions]);

  // Question analytics
  const questionStats = useMemo(() => {
    const fieldMap: Record<string, { label: string; total: number; answered: number }> = {};
    forms.forEach((f) => {
      try {
        const fields = Array.isArray(f.fields) ? f.fields : JSON.parse(f.fields || '[]');
        fields.forEach((fi: any) => {
          const key = fi.id || fi.name || fi.label;
          if (!fieldMap[key]) fieldMap[key] = { label: fi.label || fi.name || key, total: 0, answered: 0 };
        });
      } catch { /* ignored */ }
    });
    submissions.forEach((s) => {
      const data = typeof s.data === 'string' ? JSON.parse(s.data) : s.data;
      if (!data) return;
      Object.keys(fieldMap).forEach((key) => {
        fieldMap[key].total++;
        if (data[key] !== undefined && data[key] !== null && data[key] !== '') {
          fieldMap[key].answered++;
        }
      });
    });
    return Object.values(fieldMap).map((q) => ({
      label: q.label,
      views: q.total || totalViews,
      answers: q.answered,
      dropOff: q.total > 0 ? Math.round(((q.total - q.answered) / q.total) * 100) : 0,
    }));
  }, [forms, submissions, totalViews]);

  // Response table
  const responseRows = useMemo(() => {
    return submissions.map((s) => {
      const data = typeof s.data === 'string' ? JSON.parse(s.data) : s.data;
      const summary = data ? Object.values(data).filter(Boolean).slice(0, 3).join(', ') : '';
      // Find referrer from views for same form around same time
      const matchingView = views.find((v) => v.form_id === s.form_id);
      return {
        id: s.id,
        submittedAt: s.submitted_at,
        summary: summary.length > 60 ? summary.slice(0, 60) + '…' : summary,
        completionTime: s.completion_time,
        source: categorizeSource(matchingView?.referrer || null),
      };
    });
  }, [submissions, views]);

  // Traffic sources
  const trafficData = useMemo(() => {
    const counts: Record<string, number> = {};
    views.forEach((v) => {
      const src = categorizeSource(v.referrer);
      counts[src] = (counts[src] || 0) + 1;
    });
    return Object.entries(counts).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [views]);

  // Device analytics
  const deviceData = useMemo(() => {
    const counts: Record<string, number> = { Desktop: 0, Mobile: 0, Tablet: 0 };
    [...views, ...submissions].forEach((item) => {
      const dt = (item.device_type || '').toLowerCase();
      if (dt.includes('mobile')) counts.Mobile++;
      else if (dt.includes('tablet')) counts.Tablet++;
      else if (dt) counts.Desktop++;
    });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [views, submissions]);

  // Export
  const exportData = () => {
    const csv = [
      ['Metric', 'Value'],
      ['Views', totalViews],
      ['Starts', formStarts],
      ['Responses', responses],
      ['Completion Rate', completionRate + '%'],
      ['Conversion Rate', conversionRate + '%'],
      ['Avg Completion Time', avgCompletionTime + 's'],
    ].map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `analytics-${timeRange}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="p-6 flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading analytics...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Helmet>
        <title>Analytics | Bravax Forms</title>
      </Helmet>
      <div className="p-4 md:p-6 space-y-6 max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground flex items-center gap-2">
              Analytics
              <Sparkles className="h-6 w-6 text-amber-500 hidden sm:block" />
            </h1>
            <p className="text-sm text-muted-foreground">Understand how your forms perform</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={selectedFormId} onValueChange={setSelectedFormId}>
              <SelectTrigger className="w-48 h-9 border-amber-500/20 bg-amber-500/5 hover:bg-amber-500/10 transition-colors">
                <SelectValue placeholder="All Forms" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Forms</SelectItem>
                {forms.map(f => (
                  <SelectItem key={f.id} value={f.id}>{f.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="w-[1px] h-6 bg-border mx-1 hidden sm:block" />
            <Select value={timeRange} onValueChange={(v) => setTimeRange(v as TimeRange)}>
              <SelectTrigger className="w-28 h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="7d">7 days</SelectItem>
                <SelectItem value="30d">30 days</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
            {timeRange === 'custom' && (
              <div className="flex gap-1">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className={cn('h-9 text-xs', !customStart && 'text-muted-foreground')}>
                      <CalendarIcon className="mr-1 h-3 w-3" />
                      {customStart ? format(customStart, 'MMM d') : 'Start'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={customStart} onSelect={setCustomStart} className="p-3 pointer-events-auto" />
                  </PopoverContent>
                </Popover>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className={cn('h-9 text-xs', !customEnd && 'text-muted-foreground')}>
                      <CalendarIcon className="mr-1 h-3 w-3" />
                      {customEnd ? format(customEnd, 'MMM d') : 'End'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={customEnd} onSelect={setCustomEnd} className="p-3 pointer-events-auto" />
                  </PopoverContent>
                </Popover>
              </div>
            )}
            <Button variant="outline" size="sm" className="h-9" onClick={exportData}>
              <Download className="mr-1 h-3 w-3" />
              Export
            </Button>
          </div>
        </div>

        {/* Overview Cards */}
        <OverviewCards
          totalViews={totalViews}
          formStarts={formStarts}
          responses={responses}
          completionRate={completionRate}
          conversionRate={conversionRate}
          avgCompletionTime={avgCompletionTime}
          prevTotalViews={prevTotal}
          prevFormStarts={prevStarts}
          prevResponses={prevResp}
          prevCompletionRate={prevCompRate}
          prevConversionRate={prevConvRate}
          prevAvgCompletionTime={prevAvgCT}
        />

        {/* Performance Chart */}
        <PerformanceChart data={chartData} />

        {/* Funnel + Question Analytics */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <FunnelVisualization steps={funnelSteps} />
          <QuestionAnalytics questions={questionStats} />
        </div>

        {/* Response Table */}
        <ResponseTable responses={responseRows} />

        {/* AI Insights Section */}
        <div id="ai-insights" className="scroll-mt-20">
          {!hasAIInsights ? (
            <Card className="card-elegant border-amber-500/20 bg-gradient-to-br from-amber-500/5 to-transparent relative overflow-hidden">
               <div className="absolute top-0 right-0 p-4 opacity-10">
                 <Lock className="h-24 w-24 text-amber-500 rotate-12" />
               </div>
               <CardHeader className="text-center pt-8">
                 <div className="mx-auto bg-amber-500/20 p-3 rounded-full w-fit mb-4">
                   <Sparkles className="h-8 w-8 text-amber-600" />
                 </div>
                 <CardTitle className="text-2xl font-bold">Unlock AI Insights</CardTitle>
                 <CardDescription className="max-w-md mx-auto text-base">
                   Get deep-dive sentiment analysis, response intent patterns, and automatic summaries with our AI engine.
                 </CardDescription>
               </CardHeader>
               <CardContent className="text-center pb-8">
                 <div className="flex flex-col items-center gap-4">
                   <p className="text-sm text-muted-foreground italic">
                     "Automatically summarize 1,000+ responses in seconds."
                   </p>
                   <Button 
                     onClick={() => window.location.href = '/billing'} 
                     className="bg-amber-600 hover:bg-amber-700 text-white min-w-[200px] h-11"
                   >
                     Upgrade to Starter
                   </Button>
                 </div>
               </CardContent>
            </Card>
          ) : (
            <AIInsightsCard 
              formTitle={selectedFormId === 'all' ? 'All Forms' : forms.find(f => f.id === selectedFormId)?.title || 'Selected Form'}
              insights={
                selectedFormId === 'all' 
                ? null 
                : forms.find(f => f.id === selectedFormId)?.ai_insights
              }
              isRefreshing={isRefreshingAI}
              onRefresh={async () => {
                if (selectedFormId === 'all') {
                  toast({
                    title: "Select a form",
                    description: "Please select a specific form to generate AI insights.",
                  });
                  return;
                }
                
                setIsRefreshingAI(true);
                try {
                  const { data, error } = await supabase.functions.invoke('ai-summarize-form', {
                    body: { formId: selectedFormId }
                  });
                  
                  if (error) throw error;
                  if (data && data.success === false) {
                    throw new Error(data.message || data.error || 'AI analysis failed');
                  }
                  
                  const { data: updatedForm } = await supabase
                    .from('forms')
                    .select('ai_insights, ai_summary')
                    .eq('id', selectedFormId)
                    .single();
                    
                  setForms(prev => prev.map(f => f.id === selectedFormId ? { ...f, ...updatedForm } : f));
                  
                  toast({
                    title: "AI Analysis Complete",
                    description: "Your form insights have been updated.",
                  });
                } catch (err: any) {
                  console.error("AI Refresh error:", err);
                  toast({
                    title: "Analysis Failed",
                    description: err.message || "Failed to generate AI insights.",
                    variant: "destructive"
                  });
                } finally {
                  setIsRefreshingAI(false);
                }
              }}
            />
          )}
        </div>

        {/* Traffic + Devices */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <TrafficSources data={trafficData} />
          <DeviceAnalytics data={deviceData} />
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Analytics;
